import { ExpandMore } from '@mui/icons-material';
import { Box, ButtonBase, Tooltip } from '@mui/material';
import { type MouseEvent } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useRecoilValue } from 'recoil';
import { ApprovalStatusLabel } from '~/components/Structures/ApprovalMark';
import { RoiReadModel } from '~/domain/readmodels/StructureSetReadModel';
import { currentStructureSetState } from '~/state/atoms/structureSet/structureSetAtoms';
import { uncommittedPointCoordinatesState } from '~/state/atoms/structureSet/roiAtoms';
import PointCoordinateEditor from './PointCoordinateEditor';

/** A muted slash between two values of a line. */
function Slash() {
    return (
        <Box component="span" color="text.disabled" flexShrink={0}>
            /
        </Box>
    );
}

/**
 * The lines under a structure's name in the details card: its type, as the
 * button that opens the Type menu when the structure is editable; under it
 * its identification code when it has one; its approval status, volume in
 * cm³ ("Point" for a point) and generation algorithm; and, for a point, the
 * editor of its coordinates.
 */
export default function StructureInformation({
    roi,
    volumeCm3,
    isEditable,
    canEditGeometry,
    typeMenuOpen,
    onOpenTypeMenu,
    onPointCoordinatesChange,
}: {
    roi: RoiReadModel;
    /** The structure's volume, in cm³. */
    volumeCm3: number;
    isEditable: boolean;
    /**
     * Separate from `isEditable` (metadata): a locked ROI keeps its
     * name/type editable but must not have its point moved.
     */
    canEditGeometry: boolean;
    typeMenuOpen: boolean;
    onOpenTypeMenu: (event: MouseEvent<HTMLElement>) => void;
    onPointCoordinatesChange: (coords: {
        x: number;
        y: number;
        z: number;
    }) => void;
}) {
    const intl = useIntl();
    const currentStructureSet = useRecoilValue(currentStructureSetState);
    const liveRoi = currentStructureSet?.roiArray.find(
        (r) => r.roiNumber === roi.roiNumber
    );
    // A point being dragged in the viewport reads where it would land.
    const uncommitted = useRecoilValue(uncommittedPointCoordinatesState);
    const liveCoordinatesMm =
        uncommitted?.roiNumber === roi.roiNumber
            ? uncommitted.coordinatesMm
            : (liveRoi?.pointCoordinatesMm ?? roi.pointCoordinatesMm);

    const typeValue = (
        <Box
            component="span"
            color="text.primary"
            fontWeight={500}
            data-testid="structure-type-value"
        >
            {roi.interpretedType || intl.formatMessage({ id: 'unknown' })}
        </Box>
    );

    return (
        <Box
            display="flex"
            flexDirection="column"
            gap={0.5}
            minWidth={0}
            fontSize={12}
            lineHeight={1.4}
            color="text.secondary"
        >
            <Box display="flex" alignItems="center" gap={0.5} minWidth={0}>
                <FormattedMessage id="structure.type" />
                {isEditable ? (
                    <Tooltip
                        title={<FormattedMessage id="structure.type.change" />}
                    >
                        <ButtonBase
                            disableRipple
                            onClick={onOpenTypeMenu}
                            aria-haspopup="menu"
                            aria-expanded={typeMenuOpen}
                            data-testid="structure-type-edit-button"
                            sx={{
                                gap: 0.25,
                                height: 20,
                                pl: 0.5,
                                pr: 0.25,
                                borderRadius: 1,
                                font: 'inherit',
                                bgcolor: typeMenuOpen
                                    ? 'action.selected'
                                    : 'transparent',
                                '&:hover': {
                                    bgcolor: typeMenuOpen
                                        ? 'action.selected'
                                        : 'action.hover',
                                },
                                '&:focus-visible': {
                                    outline: 1,
                                    outlineColor: 'primary.main',
                                },
                            }}
                        >
                            {typeValue}
                            <ExpandMore
                                sx={{
                                    fontSize: 16,
                                    color: typeMenuOpen
                                        ? 'primary.main'
                                        : 'text.secondary',
                                }}
                            />
                        </ButtonBase>
                    </Tooltip>
                ) : (
                    typeValue
                )}
            </Box>
            {roi.identificationCode && (
                <Box display="flex" gap={0.5} minWidth={0} whiteSpace="nowrap">
                    <Box component="span" flexShrink={0}>
                        <FormattedMessage id="structure.identificationCode" />
                    </Box>
                    <Box
                        component="span"
                        color="text.primary"
                        fontWeight={500}
                        minWidth={0}
                        overflow="hidden"
                        textOverflow="ellipsis"
                        data-testid="structure-identification-code-value"
                    >
                        {roi.identificationCode.codeMeaning}
                    </Box>
                </Box>
            )}
            <Box
                display="flex"
                alignItems="center"
                gap={0.75}
                minWidth={0}
                overflow="hidden"
                whiteSpace="nowrap"
            >
                <Box component="span" flexShrink={0} display="inline-flex">
                    <ApprovalStatusLabel
                        status={roi.approvalStatus}
                        size={14}
                    />
                </Box>
                <Slash />
                <Box
                    component="span"
                    flexShrink={0}
                    data-testid="structure-volume-value"
                >
                    {roi.isPointRoi ? (
                        <FormattedMessage id="structure.pointVolume" />
                    ) : (
                        <>
                            <Box
                                component="span"
                                color="text.primary"
                                fontFamily="fontFamilyMonospace"
                            >
                                {volumeCm3.toFixed(2)}
                            </Box>{' '}
                            cm³
                        </>
                    )}
                </Box>
                <Slash />
                <Box
                    component="span"
                    minWidth={0}
                    overflow="hidden"
                    textOverflow="ellipsis"
                >
                    {roi.roiGenerationAlgorithm}
                </Box>
            </Box>
            {roi.isPointRoi && liveCoordinatesMm != null && (
                <Box mt={0.5}>
                    <PointCoordinateEditor
                        coordinatesMm={liveCoordinatesMm}
                        onChange={onPointCoordinatesChange}
                        disabled={!canEditGeometry}
                    />
                </Box>
            )}
        </Box>
    );
}
